import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import {
  formatRangeWindowNoDataText,
  resolveQualityComparisonWindowLabel,
  useQualityDateRange,
} from '@/features/quality/hooks';
import { useReleasesPageViewModel } from '@/features/releases/hooks/useReleasesPageViewModel';
import { ReleasesTablePanel } from '@/features/releases/pages/ReleasesTablePanel';
import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import { SearchTagsDateHeaderFiltersNext } from '@/features/shared/components/entityTrends/SearchTagsDateHeaderFiltersNext';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { ReleasesIcon, Text } from '@/ui/primitives';
import type { ReleaseStatus } from '@flowqa/shared';

const resolveReleasesStatusCardIcon = (statusId: string): typeof ReleasesIcon => {
  if (statusId === 'total') return ReleasesIcon;
  return resolveGenericStatusCardIcon(statusId);
};

const resolveSummaryCount = (value: ReactNode): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const parsedValue = Number.parseInt(value.replaceAll(',', ''), 10);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const ReleasesPage = (): ReactElement => {
  const {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    tagOptionsLoading,
    projectFilterOptions,
    showProjectFilter,
    onStatusesChange,
    onTagsChange,
    onProjectsChange,
    tableFilters,
    releaseMetricCards,
    releaseMetricCardsLoading,
  } = useReleasesPageViewModel();
  const [searchValue, setSearchValue] = useState('');
  const { rangeStartDay, rangeEndDay } = useQualityDateRange(range);
  const comparisonWindowLabel = resolveQualityComparisonWindowLabel(range);
  const totalMetricCard = releaseMetricCards.find((card) => card.id === 'total');
  const summaryCount = resolveSummaryCount(totalMetricCard?.count);
  const tableProjectOptions = useMemo(
    () =>
      projectFilterOptions.map((option) => ({
        id: option.value,
        name: option.label,
        badgeChars: '',
        badgeColor: 'neutral' as const,
        badgeEnabled: false,
      })),
    [projectFilterOptions],
  );
  const tableNoRowsText = formatRangeWindowNoDataText(
    getEmptyStateMessage('entity.noneFound', { entity: 'release' }),
    range,
    false,
  );

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Releases
        </Text>
      }
      headerActions={
        <SearchTagsDateHeaderFiltersNext
          searchAriaLabel="Search releases"
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          selectedStatuses={selectedStatuses}
          statusOptions={releaseStatusOptions}
          onStatusesChange={(next) => {
            onStatusesChange(next as ReleaseStatus[]);
          }}
          selectedTags={selectedTags}
          tagOptions={tagOptions}
          tagOptionsLoading={tagOptionsLoading}
          onTagsChange={onTagsChange}
          selectedProjects={showProjectFilter ? selectedProjects : undefined}
          onProjectsChange={showProjectFilter ? onProjectsChange : undefined}
          projectOptions={showProjectFilter ? projectFilterOptions : undefined}
          range={range}
          onRangeChange={setRange}
          pickerStartDay={rangeStartDay}
          pickerEndDay={rangeEndDay}
          hasCustomDates={false}
          isLatestMode={range === 'latest'}
          onSelectedStartDayChange={() => undefined}
          onSelectedEndDayChange={() => undefined}
          includeLatest={false}
          showCustomDateRange={false}
          summaryCount={summaryCount}
          summaryStartDay={rangeStartDay}
          summaryEndDay={rangeEndDay}
          summarySingularLabel="release"
          summaryPluralLabel="releases"
          summaryEmptyText={tableNoRowsText}
          summaryLoading={releaseMetricCardsLoading}
        />
      }
      summary={
        <StatusMetricCardsRow
          cards={releaseMetricCards}
          loading={releaseMetricCardsLoading}
          comparisonWindowLabel={comparisonWindowLabel}
          resolveStatusCardIcon={resolveReleasesStatusCardIcon}
        />
      }
      content={
        <ReleasesTablePanel
          fixedFilters={tableFilters}
          rangeStartDay={rangeStartDay}
          rangeEndDay={rangeEndDay}
          noRowsText={tableNoRowsText}
          projectOptions={tableProjectOptions}
          searchValue={searchValue}
          tagOptions={tagOptions}
        />
      }
      paddingBottom="lg"
    />
  );
};
